'use client';

import { KeyboardEvent, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import {
  CART_COUPON_APPLIED_MESSAGE,
  CART_COUPON_APPLY_LABEL,
  CART_COUPON_INPUT_LABEL,
  CART_COUPON_MAX_LENGTH,
  CART_COUPON_PLACEHOLDER,
  CART_COUPON_REMOVE_LABEL,
  cartCouponPartialMessage,
  withFinalPeriod,
} from './cart-coupon.const';

import { Button } from '@/components/ui/button/button.component';
import { Input } from '@/components/ui/input/input.component';

interface CartCouponFieldProps {
  appliedCode: string | null;
  appliedItemCount: number;
  totalItemCount: number;
  isApplying: boolean;
  isRemoving: boolean;
  errorMessage: string | null;
  onApply: (code: string) => void;
  onRemove: () => void;
  onClearError: () => void;
}

function appliedMessage(appliedItemCount: number, totalItemCount: number): string {
  return appliedItemCount < totalItemCount
    ? cartCouponPartialMessage(appliedItemCount, totalItemCount)
    : CART_COUPON_APPLIED_MESSAGE;
}

export function CartCouponField({
  appliedCode,
  appliedItemCount,
  totalItemCount,
  isApplying,
  isRemoving,
  errorMessage,
  onApply,
  onRemove,
  onClearError,
}: CartCouponFieldProps) {
  const [draft, setDraft] = useState('');

  const isApplied = appliedCode !== null;
  const value = appliedCode ?? draft;
  const canApply = draft.trim().length > 0 && !isApplying;

  function handleApply() {
    if (!canApply) {
      return;
    }

    onApply(draft.trim().toUpperCase());
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    handleApply();
  }

  function handleClear() {
    if (isApplied) {
      onRemove();
    }
  }

  return (
    <div className="flex w-full flex-col gap-1">
      <div className="flex w-full items-center gap-2">
        <Input
          value={value}
          readOnly={isApplied}
          disabled={isRemoving}
          hasError={Boolean(errorMessage)}
          maxLength={CART_COUPON_MAX_LENGTH}
          placeholder={CART_COUPON_PLACEHOLDER}
          aria-label={CART_COUPON_INPUT_LABEL}
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputClassName="uppercase placeholder:normal-case"
          className={isApplied ? 'flex-1 border-success-500 focus-within:border-success-500' : 'flex-1'}
          cleanValueDataTestId={
            isApplied ? DataTestIdEnum.CART_COUPON_REMOVE_BUTTON : DataTestIdEnum.INPUT_CLEAR_BUTTON
          }
          clearLabel={isApplied ? CART_COUPON_REMOVE_LABEL : undefined}
          onChange={(event) => {
            setDraft(event.target.value);
            onClearError();
          }}
          onClear={handleClear}
          onKeyDown={handleKeyDown}
          data-testid={DataTestIdEnum.CART_COUPON_INPUT}
        />

        {!isApplied && (
          <Button
            className="w-auto shrink-0"
            disabled={!canApply}
            isLoading={isApplying}
            onClick={handleApply}
            data-testid={DataTestIdEnum.CART_COUPON_APPLY_BUTTON}
          >
            {CART_COUPON_APPLY_LABEL}
          </Button>
        )}
      </div>

      {(isApplied || errorMessage) && (
        <p
          className={`text-sm font-semibold leading-5 ${errorMessage ? 'text-danger-600' : 'text-success-700'}`}
          data-testid={DataTestIdEnum.CART_COUPON_MESSAGE}
        >
          {withFinalPeriod(errorMessage ?? appliedMessage(appliedItemCount, totalItemCount))}
        </p>
      )}
    </div>
  );
}
